import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, Watch } from '@stencil/core';
import { applyFilters } from '@wordpress/hooks';

import { FormSubmitController } from '../../../functions/form-data';

let id = 0;

/**
 * @part base - The elements base wrapper.
 * @part input - The html input element.
 * @part form-control - The form control wrapper.
 * @part label - The input label.
 * @part help-text - Help text that describes how to use the input.
 * @part prefix - Used to prepend an icon or element to the input.
 * @part suffix - Used to prepend an icon or element to the input.
 */
@Component({
  tag: 'sc-phone-input',
  styleUrl: 'sc-phone-input.scss',
  shadow: true,
})
export class ScPhoneInput {
  private input: HTMLInputElement;
  private inputId: string = `phone-input-${++id}`;
  private helpId = `phone-input-help-text-${id}`;
  private labelId = `phone-input-label-${id}`;

  /** Element */
  @Element() el: HTMLScInputElement;

  private formController: any;

  @Prop() squared: boolean;
  @Prop() squaredBottom: boolean;
  @Prop() squaredTop: boolean;
  @Prop() squaredLeft: boolean;
  @Prop() squaredRight: boolean;

  /** Hidden */
  @Prop() hidden: boolean = false;

  /** The input's size. */
  @Prop({ reflect: true }) size: 'small' | 'medium' | 'large' = 'medium';

  /** The input's name attribute. */
  @Prop({ reflect: true }) name: string;

  /** The input's value attribute. */
  @Prop({ mutable: true, reflect: true }) value = '';

  /** Draws a pill-style input with rounded edges. */
  @Prop({ reflect: true }) pill = false;

  /** The input's label. */
  @Prop() label: string;

  /** Should we show the label */
  @Prop() showLabel: boolean = true;

  /** The input's help text. */
  @Prop() help: string = '';

  /** Adds a clear button when the input is populated. */
  @Prop() clearable = false;

  /** Adds a password toggle button to password inputs. */
  @Prop() togglePassword: boolean = false;

  /** The input's placeholder text. */
  @Prop() placeholder: string;

  /** Disables the input. */
  @Prop({ reflect: true, mutable: true }) disabled: boolean = false;

  /** Makes the input readonly. */
  @Prop({ reflect: true }) readonly: boolean = false;

  /** The minimum length of input that will be considered valid. */
  @Prop() minlength: number;

  /** The maximum length of input that will be considered valid. */
  @Prop() maxlength: number;

  /** The input's minimum value. */
  @Prop() min: number | string;

  /** The input's maximum value. */
  @Prop() max: number | string;

  /** The input's step attribute. */
  @Prop() step: number;

  /**
   * A pattern to validate input against. Deliberately loose: it must keep
   * accepting inputs the old (non-compiling, so never enforced) default let
   * through — letters cover extensions ("ext 12", "x22"), comma/semicolon are
   * dial-pause characters.
   */
  @Prop() pattern: string = '[0-9a-zA-Z\\s,;#+\\(\\)\\.\\/_\\-]*';

  /** Makes the input a required field. */
  @Prop({ reflect: true }) required = false;

  /**
   * This will be true when the control is in an invalid state. Validity is determined by props such as `type`,
   * `required`, `minlength`, `maxlength`, and `pattern` using the browser's constraint validation API.
   */
  @Prop({ mutable: true, reflect: true }) invalid = false;

  /** The input's autocorrect attribute. */
  @Prop() autocorrect: string;

  /** The input's autocomplete attribute. */
  @Prop() autocomplete: string;

  /** The input's autofocus attribute. */
  @Prop() autofocus: boolean;

  /** Enables spell checking on the input. */
  @Prop() spellcheck: boolean;

  /** Inputs focus */
  @Prop({ mutable: true, reflect: true }) hasFocus: boolean;

  /** Emitted when the control's value changes. */
  @Event({ composed: true })
  scChange: EventEmitter<void>;

  /** Emitted when the clear button is activated. */
  @Event() scClear: EventEmitter<void>;

  /** Emitted when the control receives input. */
  @Event({ cancelable: true }) scInput: EventEmitter<void>;

  /** Emitted when the control gains focus. */
  @Event() scFocus: EventEmitter<void>;

  /** Emitted when the control loses focus. */
  @Event() scBlur: EventEmitter<void>;

  @Method()
  async reportValidity() {
    return this.input.reportValidity();
  }

  /** Sets focus on the input. */
  @Method()
  async triggerFocus(options?: FocusOptions) {
    return this.input.focus(options);
  }

  /** Sets a custom validation message. If `message` is not empty, the field will be considered invalid. */
  @Method()
  async setCustomValidity(message: string) {
    this.input.setCustomValidity(message);
    this.invalid = !this.input.checkValidity();
  }

  /** Removes focus from the input. */
  @Method()
  async triggerBlur() {
    return this.input.blur();
  }

  /** Selects all the text in the input. */
  select() {
    return this.input.select();
  }

  handleBlur() {
    this.hasFocus = false;
    this.scBlur.emit();
  }

  handleFocus() {
    this.hasFocus = true;
    this.scFocus.emit();
  }

  handleChange() {
    this.value = this.input.value;
    this.scChange.emit();
  }

  handleInput() {
    this.value = this.input.value.replace(/\s/g, '');
    this.input.value = this.value;
    this.scInput.emit();
  }

  handleClearClick(event: MouseEvent) {
    this.value = '';
    this.scClear.emit();
    this.scInput.emit();
    this.scChange.emit();
    this.input.focus();

    event.stopPropagation();
  }

  @Watch('hasFocus')
  handleFocusChange() {
    setTimeout(() => {
      this.hasFocus && this.input ? this.input.focus() : this.input.blur();
    }, 0);
  }

  @Watch('value')
  handleValueChange() {
    if (this.input) {
      this.invalid = !this.input.checkValidity();
    }
  }

  componentDidLoad() {
    this.formController = new FormSubmitController(this.el).addFormData();
    this.handleFocusChange();
  }

  disconnectedCallback() {
    this.formController?.removeFormData();
  }

  render() {
    return (
      <Host hidden={this.hidden}>
        <sc-form-control
          exportparts="label, help-text, form-control"
          size={this.size}
          required={this.required}
          label={this.label}
          showLabel={this.showLabel}
          help={this.help}
          inputId={this.inputId}
          helpId={this.helpId}
          labelId={this.labelId}
          name={this.name}
          aria-label={this.label}
        >
          <slot name="label-end" slot="label-end"></slot>
          <div
            part="base"
            class={{
              'input': true,

              // Sizes
              'input--small': this.size === 'small',
              'input--medium': this.size === 'medium',
              'input--large': this.size === 'large',

              // States
              'input--focused': this.hasFocus,
              'input--invalid': this.invalid,
              'input--disabled': this.disabled,

              'input--squared': this.squared,
              'input--squared-bottom': this.squaredBottom,
              'input--squared-top': this.squaredTop,
              'input--squared-left': this.squaredLeft,
              'input--squared-right': this.squaredRight,
            }}
          >
            <span part="prefix" class="input__prefix">
              <slot name="prefix"></slot>
            </span>

            <slot>
              <input
                part="input"
                id={this.inputId}
                class="input__control"
                ref={el => (this.input = el as HTMLInputElement)}
                type="tel"
                name={this.name}
                disabled={this.disabled}
                readonly={this.readonly}
                required={this.required}
                placeholder={this.placeholder}
                minlength={this.minlength}
                maxlength={this.maxlength}
                min={this.min}
                max={this.max}
                step={this.step}
                // TODO: Test These below
                autocomplete={'tel'}
                autocorrect={this.autocorrect}
                autofocus={this.autofocus}
                spellcheck={this.spellcheck}
                pattern={applyFilters('surecart/sc-phone-input/pattern', this.pattern) as string}
                inputmode={'numeric'}
                aria-label={this.label}
                aria-labelledby={this.label}
                aria-invalid={this.invalid ? true : false}
                value={this.value}
                onChange={() => this.handleChange()}
                onInput={() => this.handleInput()}
                onFocus={() => this.handleFocus()}
                onBlur={() => this.handleBlur()}
              />
            </slot>

            <span part="suffix" class="input__suffix">
              <slot name="suffix"></slot>
            </span>

            {this.clearable && this.value?.length > 0 && (
              <button part="clear-button" class="input__clear" type="button" onClick={e => this.handleClearClick(e)} tabindex="-1">
                <slot name="clear-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="16"
                    height="16"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    class="feather feather-x"
                  >
                    <line x1="18" y1="6" x2="6" y2="18"></line>
                    <line x1="6" y1="6" x2="18" y2="18"></line>
                  </svg>
                </slot>
              </button>
            )}
          </div>
        </sc-form-control>
      </Host>
    );
  }
}
